/**
 * Browser-side .docx helpers. A .docx is a zip; we read word/document.xml and
 * work with it directly (via the bundled jszip, loaded on demand) so we never
 * need a server round-trip to open a Word SRS/VEP/SDS.
 */

const decodeXmlEntities = (s) => s
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&#39;/g, "'")
  .replace(/&quot;/g, '"');

/**
 * Convert the raw word/document.xml of a .docx into readable plain text: table
 * rows become newlines, cells are pipe-separated, tabs preserved, tags stripped,
 * entities decoded.
 */
export const xmlToPlainText = (xml) => {
  const stripped = decodeXmlEntities(
    xml
      .replace(/<\/w:tr>/g, '\n')
      .replace(/<\/w:tc>/g, ' | ')
      .replace(/<\/w:p>/g, '\n')
      .replace(/<w:tab\/>/g, '\t')
      .replace(/<[^>]+>/g, ''),
  );
  return stripped
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').replace(/\s*\|\s*/g, ' | ').trim().replace(/^\|\s*|\s*\|$/g, '').trim())
    .filter(Boolean)
    .join('\n');
};

/** Load the word/document.xml string from a .docx File via the bundled jszip. */
export const loadDocxXml = async (file) => {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(file);
  const entry = zip.file('word/document.xml');
  if (!entry) throw new Error('not a valid .docx (missing word/document.xml)');
  return entry.async('string');
};

/** Extract plain text from a .docx file (used to feed uploaded docs to the model). */
export const extractDocxText = async (file) => xmlToPlainText(await loadDocxXml(file));

/**
 * Parse a .docx into a reusable template: `exemplar` is the full text (the
 * "golden example" the AI should match) and `sections` are derived from the
 * document's Heading/Title-styled paragraphs so the existing section pipeline
 * still applies. Returns { exemplar, sections } (sections may be empty).
 */
export const extractDocxTemplate = async (file) => {
  const xml = await loadDocxXml(file);
  const exemplar = xmlToPlainText(xml);

  const sections = [];
  const paraRe = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g;
  let match;
  while ((match = paraRe.exec(xml)) !== null) {
    const p = match[0];
    const styleMatch = p.match(/<w:pStyle[^>]*w:val="([^"]+)"/);
    const style = styleMatch ? styleMatch[1] : '';
    if (!/^heading[1-9]/i.test(style) && !/^title$/i.test(style)) continue;
    const title = decodeXmlEntities(
      p.replace(/<w:tab\/>/g, ' ').replace(/<[^>]+>/g, ''),
    ).replace(/\s+/g, ' ').trim();
    if (title) sections.push({ title, guidance: '' });
  }
  return { exemplar, sections };
};
